import { useMemo, useState, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ToggleBox } from '@unified-checkout/design-system';

import type { IFields } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import {
  ADDITIONAL_INFO,
  ADDITIONAL_INFO_KEYS,
  SINGLE_PAYMENTS_FIELDS,
} from '@app/dashboard/constants/payment-links/single-payments';

import CountriesDropdown from '../CountriesDropdown';

const AdditionalInfoToggles = ({
  fields,
  setFields,
  isShouldFilledToggleOff,
  customerDetailsType,
}: IFields & {
  isShouldFilledToggleOff: boolean;
  customerDetailsType: string;
}) => {
  const { t } = useTranslation();

  const [isTouched, setIsTouched] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [additionalInfoToggles, setAdditionalInfoToggles] = useState<string[]>(
    []
  );

  const additionalInfoOptions = useMemo(() => {
    const options = ADDITIONAL_INFO(t);

    if (isShouldFilledToggleOff) {
      return options.filter(
        (option) => option.id === ADDITIONAL_INFO_KEYS.SHIPPING_ADDRESS
      );
    }

    return options;
  }, [isShouldFilledToggleOff, t]);

  const handleAdditionalInfoToggle = ({
    label,
    checked,
  }: {
    label: string;
    checked: boolean;
  }) => {
    if (!isTouched) return;

    setFields((prev: any) => {
      const updatedFields = { ...prev };
      if (checked) {
        updatedFields[label] = {
          value: true,
        };
      } else {
        if (label === SINGLE_PAYMENTS_FIELDS.COLLECT_SHIPPING_DETAILS) {
          delete updatedFields[SINGLE_PAYMENTS_FIELDS.ALLOWED_COUNTRIES];
        }

        delete updatedFields[label];
      }
      return updatedFields;
    });

    setAdditionalInfoToggles((prev) => {
      if (checked && !prev.includes(label)) {
        return [...prev, label];
      } else {
        return prev.filter((item) => item !== label);
      }
    });
  };

  const handleTouchedToggle = () => {
    if (!isTouched) {
      setIsTouched(true);
    }
    return Promise.resolve(true);
  };

  useEffect(() => {
    setResetKey((prev) => prev + 1);
    setIsTouched(false);
    setAdditionalInfoToggles([]);
  }, [customerDetailsType]);

  return additionalInfoOptions.map((item) => {
    const shouldShowShippingAddressDropdown =
      item.id === ADDITIONAL_INFO_KEYS.SHIPPING_ADDRESS &&
      additionalInfoToggles.includes(item.value);

    return (
      <Fragment key={item.id}>
        <ToggleBox
          text={item.text}
          checked={additionalInfoToggles.includes(item.id)}
          onToggleChangeHandler={(checked) =>
            handleAdditionalInfoToggle({
              label: item.value,
              checked,
            })
          }
          onBeforeToggleConfirmation={handleTouchedToggle}
          key={resetKey}
        />
        {shouldShowShippingAddressDropdown && (
          <CountriesDropdown fields={fields} setFields={setFields} />
        )}
      </Fragment>
    );
  });
};

export default AdditionalInfoToggles;
